import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, Zap, Eye, Search, Brain, Hourglass } from 'lucide-react';
import { getKnowledgeBase, deleteKnowledgeBaseEntry } from '../data';
import './AgentPages.css';

export default function AgentKnowledgeBase() {
  const { agent, agentId } = useOutletContext();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadKnowledgeBase();
  }, [agentId]);

  const loadKnowledgeBase = async () => {
    try {
      setLoading(true);
      const data = await getKnowledgeBase(agentId);
      const sorted = (data || []).sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      });
      setEntries(sorted);
    } catch (err) {
      console.error('Failed to load knowledge base:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!confirm('Delete this knowledge base entry?')) return;
    try {
      await deleteKnowledgeBaseEntry(agentId, entryId);
      setEntries(entries.filter(e => e.id !== entryId));
    } catch (err) {
      console.error('Failed to delete entry:', err);
    }
  };

  const filteredEntries = entries.filter(entry => {
    const matchesFilter = filter === 'all' || 
      (filter === 'success' && entry.success) || 
      (filter === 'failed' && !entry.success) ||
      (filter === 'in_progress' && entry.status === 'in_progress');
    
    const matchesSearch = !searchQuery || 
      entry.goal_summary?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.topic_keywords?.some(k => k.toLowerCase().includes(searchQuery.toLowerCase()));
    
    return matchesFilter && matchesSearch;
  });

  const groupedByThread = filteredEntries.reduce((groups, entry) => {
    const threadId = entry.thread_id || entry._id || 'unknown';
    if (!groups[threadId]) {
      groups[threadId] = [];
    }
    groups[threadId].push(entry);
    return groups;
  }, {});

  const threadGroups = Object.entries(groupedByThread).map(([threadId, threadEntries]) => {
    const sortedEntries = threadEntries.sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateA - dateB;
    });
    const latestMs = Math.max(...sortedEntries.map(e => new Date(e.created_at || 0).getTime() || 0));
    return {
      threadId,
      entries: sortedEntries,
      latestMs,
      hasEscalation: threadEntries.some(e => !e.success),
    };
  }).sort((a, b) => b.latestMs - a.latestMs);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleString('en-US', {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
      hour12: true,
    });
  };

  const getTimelineIcon = (source) => {
    switch (source) {
      case 'agent': return <Bot size={14} />;
      case 'integration': return <Zap size={14} />;
      case 'evaluator': return <Eye size={14} />;
      default: return '--';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 0.8) return '#059669';
    if (score >= 0.6) return '#65a30d';
    if (score >= 0.4) return '#d97706';
    return '#dc2626';
  };

  const formatScore = (score) => {
    return Math.round((score || 0) * 100);
  };

  return (
    <div className="config-page">
      <div className="config-header">
        <h1>Knowledge Base</h1>
        <p>Past interactions your agent learns from. High-scoring successful interactions are used as reference articles, while failed interactions help avoid repeated mistakes.</p>
      </div>

      <div className="config-section">
        <div className="section-header">
          <div className="section-icon"><Search size={18} /></div>
          <div className="section-text">
            <h2>Search & Filter</h2>
            <p>Find specific interactions in your agent's memory</p>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <input
              type="text"
              className="input"
              placeholder="Search by goal or keywords..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="form-group">
            <div className="kb-filters">
              <button 
                className={`kb-filter-btn ${filter === 'all' ? 'active' : ''}`}
                onClick={() => setFilter('all')}
              >
                All ({entries.length})
              </button>
              <button 
                className={`kb-filter-btn ${filter === 'in_progress' ? 'active' : ''}`}
                onClick={() => setFilter('in_progress')}
                style={{ color: filter === 'in_progress' ? '#6366f1' : undefined }}
              >
                In Progress ({entries.filter(e => e.status === 'in_progress').length})
              </button>
              <button 
                className={`kb-filter-btn success ${filter === 'success' ? 'active' : ''}`}
                onClick={() => setFilter('success')}
              >
                Successful ({entries.filter(e => e.success).length})
              </button>
              <button 
                className={`kb-filter-btn failed ${filter === 'failed' ? 'active' : ''}`}
                onClick={() => setFilter('failed')}
              >
                Failed ({entries.filter(e => !e.success).length})
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="config-section">
        <div className="section-header">
          <div className="section-icon"><Brain size={18} /></div>
          <div className="section-text">
            <h2>Learned Interactions</h2>
            <p>Each completed email conversation becomes a searchable reference</p>
          </div>
        </div>

        {loading ? (
          <div className="empty-state">
            <div className="empty-state-icon"><Hourglass size={24} /></div>
            <h3>Loading knowledge base...</h3>
          </div>
        ) : threadGroups.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><Brain size={24} /></div>
            <h3>No entries yet</h3>
            <p>Your agent will learn from interactions and store them here.</p>
          </div>
        ) : (
          <div className="kb-entries-list">
            <AnimatePresence>
              {threadGroups.map((group, groupIndex) => (
                <motion.div
                  key={group.threadId}
                  className={`kb-thread-group ${group.hasEscalation ? 'has-escalation' : ''}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: groupIndex * 0.05 }}
                >
                  <div className="kb-thread-header">
                    <span className="kb-thread-id">Thread: {group.threadId.slice(0, 12)}...</span>
                    <span className="kb-thread-count">{group.entries.length} interaction{group.entries.length > 1 ? 's' : ''}</span>
                  </div>
                  
                  {group.entries.map((entry, index) => (
                    <motion.div
                      key={entry.id}
                      className={`kb-entry-card ${entry.success ? 'success' : 'failed'}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.02 }}
                    >
                      <div 
                        className="kb-entry-header"
                        onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                      >
                        <div className="kb-entry-badges">
                          {entry.status === 'in_progress' ? (
                            <span className="kb-status-in-progress">In Progress</span>
                          ) : (
                            <span className={`kb-status-badge ${entry.success ? 'success' : 'failed'}`}>
                              {entry.success ? 'Success' : 'Failed'}
                            </span>
                          )}
                          {entry.status !== 'in_progress' && (
                            <span 
                              className="kb-score-badge"
                              style={{ backgroundColor: getScoreColor(entry.concise_score) }}
                            >
                              {formatScore(entry.concise_score)}%
                            </span>
                          )}
                          <span className="kb-turns-badge">{entry.turn_count || '?'} turns</span>
                          {entry.evaluator_runs?.length > 0 && (
                            <span className="kb-turns-badge">{entry.evaluator_runs.length} evals</span>
                          )}
                        </div>
                        
                        <div className="kb-entry-content">
                          <h4>{entry.goal_summary || entry.goal || 'Unknown Goal'}</h4>
                          <p>{entry.resolution_summary || (entry.status === 'in_progress' ? 'Conversation in progress...' : 'No resolution summary')}</p>
                          {entry.created_at && (
                            <span style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', display: 'inline-block' }}>
                              {formatTimestamp(entry.created_at)}
                            </span>
                          )}
                        </div>

                        <span className="kb-expand-icon">{expandedId === entry.id ? '−' : '+'}</span>
                      </div>

                      {expandedId === entry.id && (
                        <motion.div 
                          className="kb-entry-details"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                        >
                          {entry.topic_keywords?.length > 0 && (
                            <div className="kb-detail-section">
                              <h5>Keywords</h5>
                              <div className="kb-keywords">
                                {entry.topic_keywords.map((keyword, i) => (
                                  <span key={i} className="kb-keyword-tag">{keyword}</span>
                                ))}
                              </div>
                            </div>
                          )}

                          {entry.step_pattern?.length > 0 && (
                            <div className="kb-detail-section">
                              <h5>Step Pattern</h5>
                              <div className="kb-step-pattern">
                                {entry.step_pattern.map((step, i) => (
                                  <span key={i} className="kb-step-tag">
                                    {step}
                                    {i < entry.step_pattern.length - 1 && <span className="kb-step-arrow"> → </span>}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {entry.message_history_summary?.length > 0 && (
                            <div className="kb-detail-section">
                              <h5>Conversation Summary</h5>
                              <ol className="kb-history-list">
                                {entry.message_history_summary.map((msg, i) => (
                                  <li key={i}>{msg}</li>
                                ))}
                              </ol>
                            </div>
                          )}

                          {entry.key_learnings?.length > 0 && (
                            <div className="kb-detail-section">
                              <h5>Key Learnings</h5>
                              <ul className="kb-learnings-list">
                                {entry.key_learnings.map((learning, i) => (
                                  <li key={i}>{learning}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {entry.timeline?.length > 0 && (
                            <div className="kb-timeline">
                              <div className="kb-timeline-header">
                                <h5>Timeline ({entry.timeline.length} events)</h5>
                              </div>
                              {entry.timeline.slice(-10).map((event, i) => (
                                <div key={i} className={`kb-timeline-event ${event.source}`}>
                                  <span className="timeline-icon">{getTimelineIcon(event.source)}</span>
                                  <div className="timeline-content">
                                    <p>{event.step_summary}</p>
                                    <span>{formatTimestamp(event.timestamp)}</span>
                                  </div>
                                </div>
                              ))}
                              {entry.timeline.length > 10 && (
                                <p style={{ fontSize: '12px', color: '#94a3b8', textAlign: 'center', marginTop: '8px' }}>
                                  Showing last 10 of {entry.timeline.length} events
                                </p>
                              )}
                            </div>
                          )}

                          {entry.evaluator_runs?.length > 0 && (
                            <div className="kb-evaluator-runs">
                              <h5>Evaluator Runs ({entry.evaluator_runs.length})</h5>
                              {entry.evaluator_runs.slice(-5).map((run, i) => (
                                <div key={i} className="evaluator-run-item">
                                  <span className={`evaluator-alignment ${run.goal_alignment}`}>
                                    {run.goal_alignment}
                                  </span>
                                  <span style={{ fontSize: '12px', color: '#64748b' }}>
                                    Confidence: {Math.round((run.confidence || 0) * 100)}%
                                  </span>
                                  {run.next_step && (
                                    <span style={{ fontSize: '12px', color: '#475569', marginLeft: 'auto' }}>
                                      Next: {run.next_step.slice(0, 50)}...
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="kb-entry-actions">
                            <button 
                              className="btn btn-sm btn-outline"
                              onClick={(e) => { e.stopPropagation(); handleDelete(entry.id); }}
                              style={{ color: '#dc2626', borderColor: '#fecaca' }}
                            >
                              Delete Entry
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </motion.div>
                  ))}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}
